import { FieldDef, GroupDef, GuiDef, ValidatorDef, TabDefinition, CollapsableDef } from '@/objects/gui';
import { Timepenalty } from '@/objects/generated';
import { markRaw, computed } from 'vue';
import {
    InputText,
    ValidatedText,
    InputTimespan,
    DisplayTimespan,
    InputFloat,
    ValidatedFloat,
    InputSelect,
    DataSourceLookup,
} from '@/components';
import { type DateType } from '@/utilities/dateutils';
import { useEventCommonStore } from '@/stores';
import { storeToRefs } from 'pinia';
import type { SelectLine, SelectLineValueType } from '@/objects/SelectLine';

export class TimepenaltyGuiDef extends GuiDef<Timepenalty> {
    constructor() {
        super('Timepenalty');

        const EventCommon = storeToRefs(useEventCommonStore());
        const StationRolesWithNoneSource = computed(() => {
            return EventCommon.StationRolesWithNoneAsSelectLine.value;
        });

        const Reason = new FieldDef<Timepenalty>();
        Reason.Name = 'Reason';
        Reason.FieldName = 'Reason';
        Reason.DefinedFor = 'Both';
        Reason.TranslationKey = 'timepenalty.reason';
        Reason.Editable = true;
        Reason.Filterable = false;
        Reason.Sortable = false;
        Reason.CssClasses = '';
        Reason.value = (timepenalty) => timepenalty.Reason;
        Reason.setValue = (timepenalty, value) => (timepenalty.Reason = value);
        Reason.DetailEditor = markRaw(InputText);
        Reason.DetailEditorProps = {};
        Reason.CellEditor = markRaw(InputText);
        Reason.CellEditorProps = {};
        Reason.CellRenderer = markRaw(ValidatedText);
        Reason.CellRendererProps = {};
        Reason.Validator = null;
        Reason.SortDirection = undefined;
        Reason.cellClassRules = undefined;
        Reason.MinWidth = 500;
        Reason.MaxWidth = undefined;
        Reason.Tooltip = null;
        this.Fields.push(Reason);

        const Penalty = new FieldDef<Timepenalty>();
        Penalty.Name = 'Penalty';
        Penalty.FieldName = 'Penalty';
        Penalty.DefinedFor = 'Both';
        Penalty.TranslationKey = 'timepenalty.penalty';
        Penalty.Editable = true;
        Penalty.Filterable = false;
        Penalty.Sortable = true;
        Penalty.CssClasses = '';
        Penalty.value = (timepenalty) => timepenalty.Penalty;
        Penalty.setValue = (timepenalty, value) => (timepenalty.Penalty = value);
        Penalty.DetailEditor = markRaw(InputTimespan);
        Penalty.DetailEditorProps = { format: 'TIME' as DateType };
        Penalty.CellEditor = markRaw(InputTimespan);
        Penalty.CellEditorProps = { format: 'TIME' as DateType };
        Penalty.CellRenderer = markRaw(DisplayTimespan);
        Penalty.CellRendererProps = { format: 'TIME' as DateType };
        Penalty.Validator = null;
        Penalty.SortDirection = undefined;
        Penalty.cellClassRules = undefined;
        Penalty.MinWidth = 100;
        Penalty.MaxWidth = 100;
        Penalty.Tooltip = null;
        this.Fields.push(Penalty);

        const Km = new FieldDef<Timepenalty>();
        Km.Name = 'Km';
        Km.FieldName = 'Km';
        Km.DefinedFor = 'Both';
        Km.TranslationKey = 'timepenalty.km';
        Km.Editable = true;
        Km.Filterable = false;
        Km.Sortable = true;
        Km.CssClasses = '';
        Km.value = (timepenalty) => timepenalty.Km;
        Km.setValue = (timepenalty, value) => {
            if (value === null || value === undefined || value === '' || isNaN(value)) {
                value = 0;
            }
            timepenalty.Km = value;
        };
        Km.DetailEditor = markRaw(InputFloat);
        Km.DetailEditorProps = {};
        Km.CellEditor = markRaw(InputFloat);
        Km.CellEditorProps = {};
        Km.CellRenderer = markRaw(ValidatedFloat);
        Km.CellRendererProps = { decimal: 3 };
        Km.Validator = null;
        Km.SortDirection = undefined;
        Km.cellClassRules = undefined;
        Km.MinWidth = 100;
        Km.MaxWidth = 100;
        Km.Tooltip = null;
        this.Fields.push(Km);

        const StationRoleID = new FieldDef<Timepenalty>();
        StationRoleID.Name = 'StationRoleID';
        StationRoleID.FieldName = 'StationRoleID';
        StationRoleID.DefinedFor = 'Both';
        StationRoleID.TranslationKey = 'timepenalty.stationroleid';
        StationRoleID.Editable = true;
        StationRoleID.Filterable = true;
        StationRoleID.Sortable = true;
        StationRoleID.CssClasses = '';
        StationRoleID.value = (timepenalty) => timepenalty.StationRoleID;
        StationRoleID.setValue = (timepenalty, value) => {
            if (value === undefined || value === '' || isNaN(value)) {
                value = null;
            }
            timepenalty.StationRoleID = value;
        };
        StationRoleID.DetailEditor = markRaw(InputSelect);
        StationRoleID.DetailEditorProps = { virtualScrollerMaxItems: 30, datasource: { mapsource: StationRolesWithNoneSource } };
        StationRoleID.CellEditor = markRaw(InputSelect);
        StationRoleID.CellEditorProps = {
            virtualScrollerMaxItems: 30,
            datasource: { mapsource: StationRolesWithNoneSource },
            autoOpen: true,
        };
        // @ts-expect-error @ts-ignore
        StationRoleID.CellRenderer = markRaw(DataSourceLookup);
        StationRoleID.CellRendererProps = { datasource: { mapsource: StationRolesWithNoneSource } };
        StationRoleID.ExportValueFormatter = (obj: Timepenalty) => {
            return StationRolesWithNoneSource.value.get(obj.StationRoleID)?.text;
        };
        StationRoleID.Validator = null;
        StationRoleID.SortDirection = undefined;
        StationRoleID.cellClassRules = undefined;
        StationRoleID.MinWidth = undefined;
        StationRoleID.MaxWidth = undefined;
        StationRoleID.Tooltip = null;
        this.Fields.push(StationRoleID);
    }

    public async LoadDependencies() {
        await useEventCommonStore().EnsureLoaded(['StationRoles']);
    }
}
